
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';
import { cn } from "@/lib/utils";

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  title?: string;
}

export const VideoModal: React.FC<VideoModalProps> = ({
  isOpen,
  onClose,
  videoUrl,
  title = "Demo Video"
}) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeVideoId(videoUrl);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      // Auto-hide loading after a delay
      const timer = setTimeout(() => setIsLoading(false), 2000);
      return () => clearTimeout(timer);
    } else {
      // Re-enable body scroll when modal closes
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsPlaying(false);
    onClose();
  };

  const embedUrl = videoId 
    ? `https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&playlist=${videoId}&controls=0&modestbranding=1&rel=0&showinfo=0&mute=${isMuted ? 1 : 0}&iv_load_policy=3&disablekb=1&cc_load_policy=0&fs=0&playsinline=1`
    : '';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-7xl w-[95vw] h-[95vh] p-0 bg-black border-none shadow-2xl"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)' }}
      >
        {/* Custom Header */}
        <div className="absolute top-4 left-4 right-4 z-50 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-white font-medium ml-4 opacity-80">{title}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMuted(!isMuted)}
              className="text-white hover:bg-white/10 rounded-full w-10 h-10 p-0"
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="text-white hover:bg-white/10 rounded-full w-10 h-10 p-0"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Video Container */}
        <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-900">
          {/* Ambient lighting effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/10 via-transparent to-purple-900/10 pointer-events-none"></div>
          
          {/* Loading State */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-40">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                <p className="text-white/80 font-medium">Loading AI Demo Experience...</p>
              </div>
            </div>
          )}

          {/* YouTube Video */}
          {videoId && (
            <div 
              className="relative w-full h-full max-w-6xl max-h-[80vh] aspect-video"
              style={{ containIntrinsicSize: '0 calc(100vw * 9 / 16)', minHeight: '360px' }}
            >
              <iframe
                src={embedUrl}
                width="1280"
                height="720"
                className={cn(
                  "w-full h-full rounded-lg shadow-2xl transition-opacity duration-500",
                  isLoading ? "opacity-0" : "opacity-100"
                )}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={title}
                loading="lazy"
                onLoad={() => setIsLoading(false)}
              />
              
              {/* Cinematic overlay bars */}
              <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-black/60 to-transparent pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"></div>
            </div>
          )}

          {/* AI Capability Indicators */}
          <div className="absolute bottom-6 left-6 flex items-center space-x-4 bg-black/40 backdrop-blur-sm rounded-full px-4 py-2">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-white/80 text-sm font-medium">AI Processing</span>
            </div>
            <div className="w-px h-4 bg-white/20"></div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
              <span className="text-white/80 text-sm font-medium">Real-time Analysis</span>
            </div>
          </div>

          {/* Brand Watermark */}
          <div className="absolute bottom-6 right-6 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2">
            <span className="text-white font-medium text-sm">Dialgia Group AI</span>
          </div>
        </div>

        {/* Keyboard shortcuts hint */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white/60 text-xs">
          Press <kbd className="bg-white/20 px-2 py-1 rounded text-xs">ESC</kbd> to close
        </div>
      </DialogContent>
    </Dialog>
  );
};
